import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck } from "lucide-react";

function LoginBox() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const go = async (e) => {
    e.preventDefault();
    try { await login(email, password); } catch { setErr("Wrong email or password"); }
  };
  return (
    <form onSubmit={go} className="space-y-2" data-testid="oauth-login-form">
      <div className="text-xs text-neutral-500">Sign in to continue.</div>
      <input className="w-full px-3 py-2 rounded border text-sm" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="oauth-login-email" />
      <input className="w-full px-3 py-2 rounded border text-sm" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="oauth-login-password" />
      {err && <div className="text-xs text-[var(--breach)]">{err}</div>}
      <button className="btn btn-primary w-full justify-center" data-testid="oauth-login-submit">Sign in</button>
    </form>
  );
}

export default function OAuthConsent() {
  const { user, loading } = useAuth();
  const q = Object.fromEntries(new URLSearchParams(window.location.search));
  const isAdmin = user?.role === "admin";
  const canCrm = isAdmin || user?.role === "operator";
  const def = isAdmin ? "admin" : canCrm ? "crm" : "read";
  const [scope, setScope] = useState(def);
  useEffect(() => { setScope(def); }, [def]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const decide = async (approve) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/oauth/approve", { client_id: q.client_id, redirect_uri: q.redirect_uri, code_challenge: q.code_challenge, state: q.state, resource: q.resource, scope, approve });
      window.location.href = data.redirect;
    } catch (e) { setErr(e.response?.data?.detail || "Could not approve"); setBusy(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center p-6 bg-[var(--bg)]" data-testid="oauth-consent-page">
      <div className="panel p-6 w-full max-w-sm space-y-4">
        <div className="flex items-center gap-2"><img src="/tt-logo.webp" alt="" className="h-6" /><ShieldCheck size={16} className="text-neutral-400" /></div>
        <div>
          <div className="text-lg font-medium" data-testid="oauth-client-name">{q.client_name || "An app"} wants access</div>
          <div className="text-xs text-neutral-500 mt-1">{scope === "crm" ? "It will act as you (shown as \u201cyour name (Claude)\u201d) on the Thick & Thin sales CRM." : "It will act as the service user \"Claude (API)\" on the Thick & Thin sales CRM."} Every write is audited. Admins can disconnect it in Settings → API keys.</div>
        </div>
        {loading ? null : !user ? <LoginBox /> : <>
          <div className="text-xs text-neutral-400">Signed in as {user.email}</div>
          <div className="space-y-1.5 text-sm">
            <label className={`flex items-center gap-2 ${isAdmin ? "" : "opacity-40"}`}><input type="radio" disabled={!isAdmin} checked={scope === "admin"} onChange={() => setScope("admin")} data-testid="oauth-scope-admin" />Full admin (read + write everything)</label>
            <label className={`flex items-start gap-2 ${canCrm ? "" : "opacity-40"}`}><input type="radio" className="mt-1" disabled={!canCrm} checked={scope === "crm"} onChange={() => setScope("crm")} data-testid="oauth-scope-crm" />
              <span>Edit CRM as me<span className="block text-xs text-neutral-500">Leads, tasks, notes, outcomes and module records, with your permissions and approval rules. No setup or admin.</span></span></label>
            <label className="flex items-center gap-2"><input type="radio" checked={scope === "read"} onChange={() => setScope("read")} data-testid="oauth-scope-read" />Read-only</label>
          </div>
          {err && <div className="text-xs text-[var(--breach)]" data-testid="oauth-error">{err}</div>}
          <div className="flex gap-2">
            <button className="btn flex-1 justify-center" disabled={busy} onClick={() => decide(false)} data-testid="oauth-deny-btn">Deny</button>
            <button className="btn btn-primary flex-1 justify-center" disabled={busy} onClick={() => decide(true)} data-testid="oauth-approve-btn">Allow</button>
          </div>
        </>}
      </div>
    </div>
  );
}
